/**
 * ContractManagement.jsx — 계약 관리 (어셈블리)
 *
 * 분할 구조:
 *   contractmanagement/utils.js              — 순수 함수 + Req/FormField + buildInitForm 등
 *   contractmanagement/pageStyles.js         — 페이지·행 카드 스타일
 *   contractmanagement/modalStyles.js        — 모달·폼 스타일
 *   contractmanagement/PromoItemSearchInput.jsx — 홍보물 검색 자동완성
 *   contractmanagement/WorkflowStepBadge.jsx — 업무 단계 배지
 *   contractmanagement/ContractLogModal.jsx  — 변경이력 모달
 *   contractmanagement/ContractRow.jsx       — 행 카드 (데스크톱 + 모바일)
 *   contractmanagement/ModalInfoSections.jsx — 고객정보·배송정보·담당자 섹션
 *   contractmanagement/ModalProductsSection.jsx — 계약정보 섹션
 *   contractmanagement/ModalPromoSection.jsx — 홍보물 섹션
 *   contractmanagement/ContractModal.jsx     — 계약 등록/수정 모달
 *   contractmanagement/useContractManagement.js — 상태·이펙트·핸들러 훅
 */
import React, { useEffect, useMemo, useState } from 'react';
import Pagination from '../../components/ui/Pagination';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../hooks/useOverlayClickClose';
import { useContractManagement } from './contractmanagement/useContractManagement';
import { ContractRow }    from './contractmanagement/ContractRow';
import { ContractModal }  from './contractmanagement/ContractModal';
import { ContractLogModal } from './contractmanagement/ContractLogModal';
import { ContractFieldSettingsModal } from './contractmanagement/ContractFieldSettingsModal';
import { ContractColorFilter } from './contractmanagement/ContractColorFilter';
import { apiClient } from '../../api/apiClient';
import { resolveRowState } from './contractmanagement/rowState';
import { HQ_CONTRACT_API, ContractApiContext } from './contractmanagement/contractApi';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdBlackBtn, stdOutlineBtn, STD_HEADER_TOP, StdPageSearch,
} from './adminlayout/designStandard';
import {
  plainSectionStyle, searchSectionStyle, cardTitleBarStyle, cardLabelStyle,
  searchRowStyle, searchInputStyle,
  resultCountStyle, emptyStyle,
} from './contractmanagement/pageStyles';
import {
  overlayStyle, confirmBoxStyle, confirmHeaderStyle,
  confirmBodyStyle, confirmMsgStyle, confirmSubStyle,
  confirmFooterStyle, confirmCancelBtnStyle, confirmOkBtnStyle,
} from './contractmanagement/modalStyles';

// 계약완료 목록 한 페이지에 보여줄 건수 (2026-09-16 사용자 지정)
const CONTRACTS_PER_PAGE = 10;

// api: 이 화면이 부를 주소 묶음(contractmanagement/contractApi.js). 기본은 본사 — /admin/contracts 는 지금 그대로(2026-10-15, 4-2).
//   아래 부품들(등록 창·이력·홍보물·칸 설정)은 Context 로 같은 묶음을 받는다.
export default function ContractManagement({ api = HQ_CONTRACT_API } = {}) {
  const {
    isMobile, contracts, listMeta, loadingList,
    searchQ, setSearchQ, searchResults, searching, clearSearch,
    users, salesPersons, contractTicketTypes,
    externalCategories, allProductCategories, promotionalItemsList,
    formDesignCourier,
    fieldConfig, refreshFieldConfig,
    modalOpen, setModalOpen, modalRow, isNew, isReorder, setIsReorder,
    deleteConfirmOpen, setDeleteConfirmOpen,
    deleteTargetRow,   setDeleteTargetRow,
    deleting,          setDeleting,
    logModalOpen,      setLogModalOpen,
    logModalRow,       setLogModalRow,
    handleSearch, onKeyDown,
    openEdit, openNew, openReorder,
    handleDeleteRequest, handleHistory, handleConfirmDelete,
    onSaved,
  } = useContractManagement(api);
  useBodyScrollLock(deleteConfirmOpen);
  const overlayCloseProps = useOverlayClickClose(() => { setDeleteConfirmOpen(false); setDeleteTargetRow(null); setDeleting(false); });

  // ⚙️ 설정 버튼은 **계약 관리 권한이 있을 때만** 보인다 (fail-closed).
  //   권한을 모르는 동안(로딩·실패)은 숨긴다. 실제 차단은 서버(requireContractAdmin)가 한다.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [canManageSettings, setCanManageSettings] = useState(false);
  useEffect(() => {
    let active = true;
    apiClient.get('/api/my/permissions').then((r) => {
      const codes = r?.ok && Array.isArray(r.data?.permCodes) ? r.data.permCodes : [];
      if (active) setCanManageSettings(codes.includes('CONTRACT_MANAGEMENT'));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // 배경색(상태) 필터 — 빈 Set = 전부 표시. 색을 켜면 그 상태 행만(다중 선택).
  const [activeColors, setActiveColors] = useState(() => new Set());
  const toggleColor = (key) => setActiveColors((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const visibleContracts = useMemo(
    () => (activeColors.size === 0 ? contracts : contracts.filter((r) => activeColors.has(resolveRowState(r)))),
    [contracts, activeColors],
  );

  // 계약완료 목록 페이지 나누기 (2026-09-16 사용자 요청: 10개씩). 색상 필터를 거친 결과를 기준으로 나눈다.
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(visibleContracts.length / CONTRACTS_PER_PAGE));
  // 필터·삭제로 목록이 줄어 현재 페이지가 사라지면 마지막 페이지로 당긴다
  useEffect(() => { setPage((p) => Math.min(p, totalPages)); }, [totalPages]);
  const pagedContracts = useMemo(
    () => visibleContracts.slice((page - 1) * CONTRACTS_PER_PAGE, page * CONTRACTS_PER_PAGE),
    [visibleContracts, page],
  );

  return (
    <ContractApiContext.Provider value={api}>
    <div style={{ display: 'grid', gap: 16 }}>

      {/* 헤더 — 직원 관리 표준 (텍스트 헤더 + 검정 CTA). 그리드 gap 16 이 아래 간격을 맡으므로 bottom margin 0 */}
      <div style={{ ...stdHeaderRow(isMobile), margin: isMobile ? '4px 0 0' : `${STD_HEADER_TOP}px 0 0` }}>
        <div>
          <h2 style={stdPageTitle}>계약 관리</h2>
          <div style={stdPageSubtitle}>계약 등록·수정·현황을 관리합니다.</div>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <button style={stdBlackBtn} onClick={openNew}>+ 신규 계약 등록</button>
          {canManageSettings && (
            <button
              type="button"
              style={stdOutlineBtn}
              onClick={() => setSettingsOpen(true)}
              title="계약 등록 창 설정 — 어떤 칸을 보일지 정합니다"
              aria-label="계약 등록 창 설정"
            >
              ⚙️{isMobile ? '' : ' 설정'}
            </button>
          )}
        </div>
      </div>

      {/* 검색 */}
      <div style={searchSectionStyle(isMobile)}>
        <div style={cardTitleBarStyle}>
          <span style={cardLabelStyle}>콜현황 &amp; 계약완료 검색</span>
        </div>
        <div style={searchRowStyle}>
          <StdPageSearch isMobile={isMobile} mobileStyle={searchInputStyle} type="text"
            placeholder="검색어 입력 후 Enter"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <button style={stdOutlineBtn} onClick={handleSearch} disabled={searching}>
            {searching ? '검색 중...' : '검색'}
          </button>
          {searchResults !== null && (
            <button style={stdOutlineBtn} onClick={clearSearch}>초기화</button>
          )}
        </div>
        {searchResults !== null && (
          <div style={{ marginTop: 12 }}>
            <div style={resultCountStyle}>검색결과 {searchResults.length}건</div>
            {searchResults.length === 0
              ? <div style={emptyStyle}>검색 결과가 없습니다.</div>
              : <div style={{ display: 'grid', gap: 8 }}>
                  {searchResults.map((row) => (
                    <ContractRow key={row.id} row={row} isMobile={isMobile} onEdit={openEdit} onReorder={openReorder}
                      onDeleteRequest={handleDeleteRequest}
                      onHistory={handleHistory} />
                  ))}
                </div>
            }
          </div>
        )}
      </div>

      {/* 계약 목록 */}
      <div style={plainSectionStyle(isMobile)}>
        <div style={cardTitleBarStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span style={cardLabelStyle}>계약완료 목록</span>
            <ContractColorFilter active={activeColors} onToggle={toggleColor} />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, flexShrink: 0 }}>
            {activeColors.size ? `${visibleContracts.length}/${contracts.length}건` : `${contracts.length}건`}
          </span>
        </div>
        {/* 상한(3000건)을 넘었을 때만 — 오래된 계약은 위 검색으로 찾는다(2026-10-13 사용자 결정 D3) */}
        {!loadingList && listMeta?.truncated && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 8px' }}>
            {`최근 ${Number(listMeta.cap || 0).toLocaleString()}건까지만 보입니다. 더 오래된 계약은 위 검색으로 찾아 주세요.`}
          </div>
        )}
        {loadingList
          ? <div style={emptyStyle}>불러오는 중...</div>
          : contracts.length === 0
            ? <div style={emptyStyle}>계약완료된 건이 없습니다.</div>
            : visibleContracts.length === 0
              ? <div style={emptyStyle}>선택한 색상의 계약이 없습니다.</div>
              : (
                <>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {pagedContracts.map((row) => (
                      <ContractRow key={row.id} row={row} isMobile={isMobile} onEdit={openEdit}
                        onDeleteRequest={handleDeleteRequest}
                        onHistory={handleHistory} />
                    ))}
                  </div>
                  <Pagination page={page} totalPages={totalPages} onMove={setPage} />
                </>
              )
        }
      </div>

      {/* 수정/등록 모달 */}
      <ContractModal
        open={modalOpen}
        row={modalRow}
        isNew={isNew}
        isReorder={isReorder}
        isMobile={isMobile}
        ticketTypes={contractTicketTypes}
        externalCategories={externalCategories}
        allProductCategories={allProductCategories}
        users={users}
        salesPersons={salesPersons}
        promotionalItemsList={promotionalItemsList}
        formDesignCourier={formDesignCourier}
        fieldConfig={fieldConfig}
        onClose={() => { setModalOpen(false); setIsReorder(false); }}
        onSave={onSaved}
      />

      {/* 계약 등록 창 설정 모달 — 저장하면 위 모달이 바로 새 설정으로 뜬다 */}
      <ContractFieldSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={refreshFieldConfig}
        ticketTypes={contractTicketTypes}
      />

      {/* 변경이력 모달 */}
      <ContractLogModal
        open={logModalOpen}
        contractId={logModalRow?.contract_id ?? logModalRow?.id}
        companyName={logModalRow?.company_name}
        onClose={() => { setLogModalOpen(false); setLogModalRow(null); }}
      />

      {/* 계약삭제 확인 모달 */}
      {deleteConfirmOpen && (
        <div style={overlayStyle} {...overlayCloseProps}>
          <div style={confirmBoxStyle} onClick={(e) => e.stopPropagation()}>
            <div style={confirmHeaderStyle('#dc2626')}>계약 삭제 확인</div>
            <div style={confirmBodyStyle}>
              <p style={confirmMsgStyle}>
                <strong>{deleteTargetRow?.company_name}</strong> 의 계약 정보를 삭제하시겠습니까?
              </p>
              <p style={confirmSubStyle}>
                계약 정보가 완전히 초기화되며, 일일콜현황·미팅지도에서 결과입력 전 상태로 되돌아갑니다.<br />
                이 작업은 되돌릴 수 없습니다.
              </p>
            </div>
            <div style={confirmFooterStyle}>
              <button style={confirmCancelBtnStyle} onClick={() => { setDeleteConfirmOpen(false); setDeleteTargetRow(null); setDeleting(false); }}>닫기</button>
              <button style={confirmOkBtnStyle('#dc2626')} disabled={deleting} onClick={handleConfirmDelete}>
                {deleting ? '삭제 중...' : '삭제 확인'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ContractApiContext.Provider>
  );
}
